"use client";

import { useState } from "react";
import IconArrowDown from "@/app/(components)/generics/Icons/IconArrowDown";
import IconArrowUp from "@/app/(components)/generics/Icons/IconArrowUp";
import IconCalendar from "@/app/(components)/generics/Icons/IconCalendar";
import { Input, TextInputType } from "@/app/(components)/generics/inputs/default/Input/Input";
import Popover from "@/app/(components)/generics/Popover/Popover";
import PopoverContent from "@/app/(components)/generics/Popover/PopoverContent";
import PopoverTrigger from "@/app/(components)/generics/Popover/PopoverTrigger";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { CallPeriod, CallPeriodField, CallPeriodRange } from "@/app/types/call/CallPeriod";
import { UStyles } from "@/app/utils/UStyles";
import { CALL_PERIOD_KEYS, CALL_PERIOD_ORDER } from "./PeriodFilter.logic";
import styles from "./PeriodFilter.module.css";

type Props = {
  period: CallPeriod;
  custom: CallPeriodRange;
  onChange: (period: CallPeriod, custom: CallPeriodRange) => void;
};

export default function PeriodFilter(props: Props) {
  const T = useT();
  const { period, custom, onChange } = props;
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (next: CallPeriod) => {
    onChange(next, next === CallPeriod.CUSTOM ? custom : {});

    if (next !== CallPeriod.CUSTOM) {
      setIsOpen(false);
    }
  };

  const handleRangeChange = (range: CallPeriodRange) => {
    onChange(CallPeriod.CUSTOM, {
      from: range.from || undefined,
      to: range.to || undefined,
    });
  };

  return (
    <Popover
      isVisible={isOpen}
      setIsVisible={setIsOpen}
      openAfterClick={true}
      closeWhenClickOutside={true}
      isPortaled={true}
      className={styles.popoverRoot}
    >
      <PopoverTrigger className={styles.triggerWrapper}>
        <span
          className={UStyles.classNames(
            styles.trigger,
            period !== CallPeriod.ALL && styles.triggerActive,
          )}
        >
          <IconCalendar size={14} />
          <span className={styles.triggerLabel}>{T(CALL_PERIOD_KEYS[period])}</span>
          <span className={styles.chevron}>
            {isOpen ? <IconArrowUp size={14} /> : <IconArrowDown size={14} />}
          </span>
        </span>
      </PopoverTrigger>

      <PopoverContent position={{ default: { top: "8px", right: "0px" } }}>
        <div className={styles.popoverContent}>
          <div className={styles.list}>
            {CALL_PERIOD_ORDER.map((option) => (
              <div
                key={option}
                className={UStyles.classNames(styles.item, option === period && styles.itemActive)}
                onClick={() => handleSelect(option)}
              >
                {T(CALL_PERIOD_KEYS[option])}
              </div>
            ))}
          </div>

          {period === CallPeriod.CUSTOM && (
            <div className={styles.range}>
              <Input
                type={TextInputType.DATE}
                data={custom}
                dataKey={CallPeriodField.FROM}
                label={T(TKey.CALL_ANALYSIS_PERIOD_FROM) as string}
                onChange={handleRangeChange}
              />
              <Input
                type={TextInputType.DATE}
                data={custom}
                dataKey={CallPeriodField.TO}
                label={T(TKey.CALL_ANALYSIS_PERIOD_TO) as string}
                onChange={handleRangeChange}
              />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
